import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { FadeInUp } from '../components/animations';
import { categoryList } from '../data/categoryData';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import heroImages from '../data/heroImages';
import { breadcrumbSchema } from '../utils/schema';

const CategoriesPage = () => {
  return (
    <div className="pt-20" data-testid="categories-page">
      <SEO
        title="Sourcing Categories | Taiga Sourcing"
        description="Explore our sourcing categories — apparel, tiles, bathware, home decor, furniture, textiles, handicrafts, packaging, electrical, plumbing, chemicals and custom sourcing from India."
        keywords="India sourcing categories, product sourcing India, Indian manufacturers categories"
        path="/categories"
        schema={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Categories', path: '/categories' },
        ])}
      />
      <PageHero image={heroImages.categories} data-testid="categories-hero">
        <p className="text-sm tracking-[0.3em] uppercase text-white/50 mb-4 font-medium">What We Source</p>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6"
          style={{ fontFamily: 'Cabinet Grotesk' }}
          data-testid="categories-title"
        >
          SOURCING CATEGORIES
        </h1>
        <p className="text-base lg:text-lg text-white/70 leading-relaxed max-w-3xl mx-auto">
          Taiga Sourcing connects international buyers with Indian manufacturers across multiple product categories.
        </p>
      </PageHero>

      <section className="py-24 lg:py-32 bg-white" data-testid="categories-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {categoryList.map((cat, i) => (
              <FadeInUp key={cat.slug} delay={i * 0.05}>
                <Link to={`/categories/${cat.slug}`}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ duration: 0.2 }}
                    className="group overflow-hidden bg-white border border-[#E5E0D8] hover:shadow-xl transition-all"
                    data-testid={`cat-card-${cat.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={cat.image}
                        alt={cat.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-6">
                      <h3
                        className="text-lg font-bold text-[#0B2A57] mb-2"
                        style={{ fontFamily: 'Cabinet Grotesk' }}
                      >
                        {cat.title}
                      </h3>
                      <p className="text-sm text-[#6B7280] leading-relaxed mb-4">{cat.shortDescription}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {cat.subcategories.slice(0, 4).map((sub, j) => (
                          <span key={j} className="text-xs px-2 py-1 bg-[#fafaf9] text-[#1E3D66] font-medium">
                            {sub}
                          </span>
                        ))}
                        {cat.subcategories.length > 4 && (
                          <span className="text-xs px-2 py-1 bg-[#fafaf9] text-[#6B7280]">
                            +{cat.subcategories.length - 4} more
                          </span>
                        )}
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E3D66] tracking-wider uppercase group-hover:gap-2 transition-all">
                        Explore Category <ArrowRight size={14} />
                      </span>
                    </div>
                  </motion.div>
                </Link>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#071E45] text-white" data-testid="categories-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <FadeInUp>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
              DON'T SEE YOUR PRODUCT?
            </h2>
            <p className="text-base text-white/60 leading-relaxed mb-8">
              We evaluate custom sourcing requests across a wide range of product categories.
            </p>
            <Link
              to="/request-sourcing"
              className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#0B2A57] text-sm font-semibold tracking-wider uppercase transition-all hover:shadow-xl hover:-translate-y-0.5"
            >
              Request Custom Sourcing <ArrowRight size={18} />
            </Link>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default CategoriesPage;
